import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  FormHelperText,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDay } from "@fortawesome/free-solid-svg-icons";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook";
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook";
import { formatDate } from "../../../../utils/formatDate";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const ApplicationDay = ({ schoolId, pathwayEmail, opEmail }) => {
  // Use Firestore hook to add data to 'ApplicationDay' collection
  const { addData: addFirestoreData } = useFirestore("ApplicationDay");

  // Use notification hook
  const {
    sendNotification,
    loading: notificationLoading,
    error: notificationError,
  } = useSendSchoolNotification();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Set minimum date to 2 weeks from today
  const minDate = useMemo(() => dayjs().add(14, "day"), []);

  const initialFormData = {
    schoolName: "",
    applicationDate: null,
    timeframe: "",
    numberOfStudents: "",
    computerUsage: "",
    fromName: "",
    phone: "",
    email: "",
    schoolId: schoolId || "",
    pathwayEmail: pathwayEmail || "",
    opEmail: opEmail || "",
  };

  const [formData, setFormData] = useState(initialFormData);

  // Update formData if props change
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      schoolId: schoolId || "",
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    }));
  }, [schoolId, pathwayEmail, opEmail]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prevData) => ({ ...prevData, applicationDate: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.numberOfStudents) {
      setSnackbarMessage("Number of Students Applying is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.schoolName) {
      setSnackbarMessage("School Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.applicationDate) {
      setSnackbarMessage("Date of Application Day is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.fromName) {
      setSnackbarMessage("From Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.email) {
      setSnackbarMessage("Email is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarMessage("");

    // Format date for display
    const formattedDate = formData.applicationDate
      ? formatDate(formData.applicationDate)
      : "N/A";

    const emailBodyContent = [
      `<strong>School Name:</strong> ${formData.schoolName || "N/A"}`,
      `<strong>From Name:</strong> ${formData.fromName || "N/A"}`,
      `<strong>Email:</strong> ${formData.email || "N/A"}`,
      `<strong>Phone:</strong> ${formData.phone || "N/A"}`,
      `<strong>Date of Application Day:</strong> ${formattedDate}`,
      `<strong>Timeframe:</strong> ${formData.timeframe || "N/A"}`,
      `<strong>Number of Students Applying:</strong> ${
        formData.numberOfStudents || "N/A"
      }`,
      `<strong>Computer Usage:</strong> ${formData.computerUsage || "N/A"}`,
    ].join("<br />");

    // Prepare data for Firestore
    const dataForFirestore = {
      ...formData,
      applicationDate: formData.applicationDate
        ? Timestamp.fromDate(formData.applicationDate.toDate())
        : null,
      Date_Submitted: Timestamp.now(),
    };

    // Prepare data for notification
    const dataForNotification = {
      pathwayEmail: formData.pathwayEmail,
      opEmail: formData.opEmail,
      schoolId: formData.schoolId,
      fromName: formData.fromName,
      schoolName: formData.schoolName,
      emailBodyContent: emailBodyContent,
    };

    try {
      // 1. Add data to Firestore
      await addFirestoreData(dataForFirestore);

      // 2. Send notification email
      await sendNotification(
        dataForNotification,
        "Application Day Request",
        "ApplicationDay"
      );

      // 3. Show success message and close
      setSnackbarMessage("Application Day request submitted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error submitting Application Day request:", error);
      const message = error.message || "An error occurred during submission.";
      setSnackbarMessage(`Error: ${message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    // Reset form
    setFormData({
      ...initialFormData,
      schoolId: schoolId || "",
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    });
  };

  return (
    <div>
      <Button
        variant="contained"
        startIcon={<FontAwesomeIcon icon={faCalendarDay} />}
        onClick={handleClickOpen}
        sx={{
          backgroundColor: "#6A5ACD", // Purple color
          color: "white",
          "&:hover": {
            backgroundColor: "#5A4FCF", // Darker purple on hover
          },
        }}
      >
        Application Day
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Application Day Request
        </ModalDialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <form onSubmit={handleSubmit}>
                <TextField
                  margin="dense"
                  label="School Name"
                  name="schoolName"
                  fullWidth
                  required
                  value={formData.schoolName}
                  onChange={handleChange}
                />

                <Box sx={{ mt: 2, mb: 1 }}>
                  <DatePicker
                    label="Date of Application Day (2 weeks in advance)"
                    value={formData.applicationDate}
                    onChange={handleDateChange}
                    minDate={minDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        margin: "dense",
                        helperText: "Must be at least 2 weeks from today",
                      },
                    }}
                  />
                </Box>

                <TextField
                  margin="dense"
                  label="Timeframe (4 Hours Max.)"
                  name="timeframe"
                  fullWidth
                  placeholder="e.g., 9:00 AM - 1:00 PM"
                  value={formData.timeframe}
                  onChange={handleChange}
                  helperText="Please specify the timeframe (maximum 4 hours)"
                />

                <TextField
                  margin="dense"
                  label="Number of Students Applying"
                  name="numberOfStudents"
                  type="number"
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                  value={formData.numberOfStudents}
                  onChange={handleChange}
                />

                <FormControl fullWidth margin="dense">
                  <InputLabel>Computer Usage</InputLabel>
                  <Select
                    name="computerUsage"
                    value={formData.computerUsage}
                    onChange={handleChange}
                    label="Computer Usage"
                  >
                    <MenuItem value="">
                      <em>Select an option</em>
                    </MenuItem>
                    <MenuItem value="Students will be using Chromebooks">
                      Students will be using Chromebooks
                    </MenuItem>
                    <MenuItem value="Students will be using laptops">
                      Students will be using laptops
                    </MenuItem>
                    <MenuItem value="Students will be in the computer lab using PCs">
                      Students will be in the computer lab using PCs
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  margin="dense"
                  label="From Name"
                  name="fromName"
                  fullWidth
                  required
                  value={formData.fromName}
                  onChange={handleChange}
                />

                <TextField
                  margin="dense"
                  label="Phone"
                  name="phone"
                  type="tel"
                  fullWidth
                  value={formData.phone}
                  onChange={handleChange}
                />

                <TextField
                  margin="dense"
                  label="Email"
                  name="email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleChange}
                />

              </form>
            </LocalizationProvider>
          )}
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} saveLabel="Submit" />
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ApplicationDay;
